import { Pipe, PipeTransform } from '@angular/core';
import { Translator } from '@vectronic/util';
import { DatatableColumn } from '../types/datatable-column';

function getDatatableCellValue<T extends object>(
  row: T,
  column: DatatableColumn<T>,
  isCompact?: boolean,
  untrimmed = false,
  noPadding = false,
) {
  if (!row || !column) return '';
  let value = column.translator ? column.translator(row) : row[column.fieldName as keyof T];
  if (!noPadding && column.compactModeSize && isCompact) {
    value = padCompactValue(value, column.compactModeSize, column.compactModeFloatNumber);
  } else if (!noPadding && column.normalModeSize && !isCompact) {
    value = padValue(value, column.normalModeSize);
  }
  if (!column.trim || untrimmed || column.ellipsis) return value;
  return Translator.trimString(value, column.trim);
}

function padCompactValue(value: any, compactModeSize: number, compactModeFloatNumber?: boolean) {
  if (value == undefined) value = '';
  let stringValue = String(value);
  let decimals = '';
  if (compactModeFloatNumber && stringValue.includes('.')) {
    const [integer, _decimals] = stringValue.split('.');
    decimals = `.${_decimals}`;
    stringValue = integer;
  }
  while (stringValue.length < compactModeSize) stringValue = '\u00A0' + stringValue;
  return `${stringValue}${decimals}`;
}

function padValue(value: any, normalModeSize: number) {
  if (value == undefined) value = '';
  while (String(value).length < normalModeSize) value = '\u00A0' + value;
  return value;
}

@Pipe({ name: 'datatableValue', pure: true })
export class DatatableValuePipe implements PipeTransform {
  transform<T extends object>(
    row: T,
    column: DatatableColumn<T>,
    isCompact: boolean,
    untrimmed = false,
    noPadding = false,
  ) {
    return getDatatableCellValue(row, column, isCompact, untrimmed, noPadding);
  }
}

@Pipe({ name: 'datatableTooltip', pure: true })
export class DatatableTooltipPipe implements PipeTransform {
  transform<T extends object>(row: T, column: DatatableColumn<T>): string {
    if (!row || !column) return '';
    if (column.cellHoverText) return column.cellHoverText(row);
    if (!column.trim && !column.ellipsis) return '';
    const untrimmedValue = getDatatableCellValue(row, column, undefined, true);
    if (untrimmedValue == null) return '';
    if (column.ellipsis && !column.trim) return String(untrimmedValue);
    const displayValue = column.ellipsis
      ? Translator.trimString(String(untrimmedValue), column.trim)
      : getDatatableCellValue(row, column);
    if (untrimmedValue !== displayValue) return untrimmedValue;
    else return '';
  }
}
